import React, { useEffect, useState } from 'react';

interface NotificationProps {
    message: string;
    type?: 'info' | 'success' | 'error';
    duration?: number;
    onClose: () => void;
}

const Notification: React.FC<NotificationProps> = ({
    message,
    type = 'info',
    duration = 3000,
    onClose,
}) => {
    const [isExiting, setIsExiting] = useState(false);
    const isSearchNotification = message.includes('Searching') || message.includes('search');

    useEffect(() => {
        // Use longer duration for search notifications
        const adjustedDuration = isSearchNotification ? 5000 : duration;

        const timer = setTimeout(() => {
            setIsExiting(true);

            // Wait for exit animation to complete before calling onClose
            const exitTimer = setTimeout(() => {
                onClose();
            }, 500);

            return () => clearTimeout(exitTimer);
        }, adjustedDuration);

        return () => clearTimeout(timer);
    }, [duration, onClose, isSearchNotification]);

    const getBackgroundClass = () => {
        if (isSearchNotification) {
            return 'bg-gradient-to-r from-blue-600 to-blue-400';
        }

        switch (type) {
            case 'success':
                return 'bg-gradient-to-r from-emerald-500 to-teal-500';
            case 'error':
                return 'bg-gradient-to-r from-red-500 to-rose-500';
            default:
                return 'bg-gradient-to-r from-primary to-secondary';
        }
    };

    return (
        <div
            className={`
                fixed top-5 left-1/2 transform -translate-x-1/2 z-50
                py-3 px-5 rounded-xl shadow-lg backdrop-blur-sm
                text-white font-medium
                ${getBackgroundClass()}
                ${isExiting ? 'animate-fade-out' : 'animate-fade-in'}
                ${isSearchNotification ? 'max-w-md w-5/6 text-center border-2 border-white/20' : 'max-w-sm'}
            `}
        >
            {message}
        </div>
    );
};

export default Notification;

// For use with a notification manager
export type NotificationData = {
    id: string;
    message: string;
    type: 'info' | 'success' | 'error';
}; 